import type {
  MasonryEngineActionHandler,
  MasonryEngineFormFieldNode,
  MasonryEngineNodeStateFromNode,
} from '@leagueplatform/masonry-engine';
import type {
  MasonryDriverAction,
  MasonryToggleFormFieldVisibilityAction,
} from '@leagueplatform/masonry-renderers';

/**
 * This action handler is responsible for toggling the visibility of form fields
 * based on the value of another field in the form.
 */
export const MasonryToggleFormFieldVisibilityActionHandler: MasonryEngineActionHandler<
  MasonryToggleFormFieldVisibilityAction,
  MasonryDriverAction
> = async (action, { invokeAction, stateController }) => {
  const {
    // allNodes - includes every node affected by any of the defined rules.
    // showNodes - defines the conditions under which a specific list of nodes should be visible.
    payload: { allNodes, showNodes },
  } = action;

  const activeFieldNodeId = action.ancestry[0];

  const activeFieldNode = stateController.getNodeState(
    activeFieldNodeId,
  ) as MasonryEngineNodeStateFromNode<MasonryEngineFormFieldNode>;

  const activeFieldValue = activeFieldNode?.value;

  const hideNodesList = new Set(allNodes);
  const showNodesList = new Set<string>();

  let showNodeFromActiveFieldValue;

  if (activeFieldValue !== undefined) {
    showNodeFromActiveFieldValue = showNodes.find(
      ({ value }) =>
        value === activeFieldValue ||
        // Checking the value for checkbox field where it is an array of strings
        (Array.isArray(activeFieldValue) && activeFieldValue.includes(value)),
    );
  }

  // Update visibility lists based on matching values between activeFieldValue and showNodes
  if (showNodeFromActiveFieldValue) {
    showNodeFromActiveFieldValue.nodes?.forEach((nodeId) => {
      hideNodesList.delete(nodeId);
      showNodesList.add(nodeId);
    });
  }

  // Hiding all nodes in the hideNodesList and clearing their values if any
  if (hideNodesList.size > 0) {
    // Reset field values via stateController before hiding
    hideNodesList.forEach((nodeId) => {
      stateController.setNodeState(nodeId, (state) => ({
        ...state,
        value: undefined, // Reset the value of the field node's which are being hidden
      }));
    });
    // Use invokeAction to hide nodes, matching the pattern used for showing
    await invokeAction({
      type: 'hide',
      ancestry: action.ancestry,
      namespace: 'default',
      payload: {
        nodeIds: Array.from(hideNodesList),
      },
    });
  }

  // Showing all nodes in the showNodesList by invoking 'show' action
  if (showNodesList.size > 0) {
    await invokeAction({
      type: 'show',
      ancestry: action.ancestry,
      namespace: 'default',
      payload: {
        nodeIds: Array.from(showNodesList),
      },
    });
  }
};
